import React from "react";
import { motion } from "framer-motion";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { initialTimeline } from "../data/initialData";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const TimelineItem = ({ item, index }) => {
  const isLeft = index % 2 === 0;

  return (
    <div className="relative flex items-start group">
      {/* Desktop: alternating layout */}
      <div className="hidden md:grid md:grid-cols-2 w-full">
        {/* Left side content or spacer */}
        <div className={`relative ${isLeft ? 'pr-10' : 'pl-10'}`}>
          {isLeft ? (
            <motion.div
              variants={itemVariants}
              className="glass-card rounded-xl p-5 hover:border-cyan-500/20 transition-all duration-300 mr-6"
            >
              <div className="flex items-start gap-3 mb-2">
                <span className="text-xs font-mono text-cyan-500 font-semibold tracking-wider flex-shrink-0 mt-0.5">
                  {item.startDate || item.year || ''}{item.endDate ? ` - ${item.endDate}` : ''}
                </span>
                <span className="text-base">{item.icon}</span>
              </div>
              <h3 className="text-base font-semibold text-white mb-0.5">{item.title}</h3>
              <p className="text-xs text-cyan-400/80 font-mono mb-3">{item.subtitle}</p>
              <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
            </motion.div>
          ) : (
            <div className="invisible" aria-hidden="true" />
          )}
        </div>

        {/* Center dot */}
        <div className="absolute left-1/2 top-0 -translate-x-1/2 z-10">
          <div className="w-5 h-5 rounded-full bg-slate-900 border-2 border-cyan-500/50 flex items-center justify-center group-hover:border-cyan-400 transition-colors">
            <div className="w-2 h-2 rounded-full bg-cyan-400" />
          </div>
        </div>

        {/* Right side content or spacer */}
        <div className={`relative ${isLeft ? 'pl-10' : 'pr-10'}`}>
          {!isLeft ? (
            <motion.div
              variants={itemVariants}
              className="glass-card rounded-xl p-5 hover:border-cyan-500/20 transition-all duration-300 ml-6"
            >
              <div className="flex items-start gap-3 mb-2">
                <span className="text-xs font-mono text-cyan-500 font-semibold tracking-wider flex-shrink-0 mt-0.5">
                  {item.startDate || item.year || ''}{item.endDate ? ` - ${item.endDate}` : ''}
                </span>
                <span className="text-base">{item.icon}</span>
              </div>
              <h3 className="text-base font-semibold text-white mb-0.5">{item.title}</h3>
              <p className="text-xs text-cyan-400/80 font-mono mb-3">{item.subtitle}</p>
              <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
            </motion.div>
          ) : (
            <div className="invisible" aria-hidden="true" />
          )}
        </div>
      </div>

      {/* Mobile: single column */}
      <div className="md:hidden relative pl-12 w-full">
        <div className="absolute left-[19px] top-1 w-[25px] h-[25px] rounded-full bg-slate-900 border-2 border-cyan-500/50 flex items-center justify-center -translate-x-1/2 z-10">
          <div className="w-2 h-2 rounded-full bg-cyan-400" />
        </div>
        <motion.div
          variants={itemVariants}
          className="glass-card rounded-xl p-5 hover:border-cyan-500/20 transition-all duration-300"
        >
          <div className="flex items-start gap-3 mb-2">
            <span className="text-xs font-mono text-cyan-500 font-semibold tracking-wider flex-shrink-0 mt-0.5">
              {item.startDate || item.year || ''}{item.endDate ? ` - ${item.endDate}` : ''}
            </span>
            <span className="text-base">{item.icon}</span>
          </div>
          <h3 className="text-base font-semibold text-white mb-0.5">{item.title}</h3>
          <p className="text-xs text-cyan-400/80 font-mono mb-3">{item.subtitle}</p>
          <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
        </motion.div>
      </div>
    </div>
  );
};

const Timeline = () => {
  const [timeline] = useLocalStorage('timeline', initialTimeline);

  return (
    <motion.section
      id="timeline"
      className="py-24 relative overflow-hidden bg-slate-950"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={containerVariants}
    >
      <div className="absolute inset-0 bg-grid opacity-20" />

      <div className="section-container relative z-10">
        <motion.div className="text-center mb-16" variants={itemVariants}>
          <motion.span
            className="inline-block px-4 py-1.5 mb-4 text-xs font-mono tracking-wider text-cyan-400 border border-cyan-500/30 rounded-full bg-cyan-500/5"
          >
            Journey
          </motion.span>
          <motion.h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Professional <span className="gradient-text">Timeline</span>
          </motion.h2>
          <motion.p className="text-slate-400 max-w-2xl mx-auto text-base">
            My professional journey in engineering
          </motion.p>
        </motion.div>

        {/* Desktop: Center line */}
        <div className="hidden md:block absolute left-1/2 top-44 bottom-24 w-px bg-gradient-to-b from-cyan-500/50 via-blue-500/30 to-transparent -translate-x-1/2" />

        {/* Mobile: Left line */}
        <div className="md:hidden absolute left-[19px] top-44 bottom-24 w-px bg-gradient-to-b from-cyan-500/50 via-blue-500/30 to-transparent" />

        <div className="relative max-w-5xl mx-auto">
          <motion.div className="space-y-8 md:space-y-12" variants={containerVariants}>
            {timeline.map((item, index) => (
              <TimelineItem key={(item.startDate || item.year || '') + index} item={item} index={index} />
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default Timeline;
